import React, { forwardRef, useImperativeHandle, useMemo, useRef } from 'react'
import { observer } from 'mobx-react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { Block, Heading, Paragraph, Text } from 'suomifi-ui-components'
import { useStore } from 'todistukset-ui/src/vtj/stores/store.util'

import { isEidasIdentifiedUser } from 'todistukset-common/src/vtj/user.utils'
import {
  isVtjOteTodistus,
  VtjOteTodistusType,
} from 'todistukset-common/src/vtj/CertificateTypes.enum'
import type { TodistusStoreForm } from 'todistukset-common/src/vtj/types/form-types.type'
import { StepComponent } from 'todistukset-ui/src/vtj/components/order-wizard/OrderWizardStep.type'
import {
  FirstErrorImperativeHandle,
  getErrorStatus,
  useFirstErrorImperativeHandle,
} from 'todistukset-ui/src/vtj/components/order-wizard/errors.util'

import getFormActions from 'todistukset-ui/src/vtj/stores/actions/get-form-actions'
import { getIsAURA } from 'todistukset-common/src/vtj/form-check.util'
import { populateAndSanitizeMailDelivery } from 'todistukset-ui/src/vtj/stores/util/form.util'
import {
  validateContactInfoEmail,
  validateContactInfoPhoneNumber,
  validateMailDeliveryCountry,
  validateMailDeliveryPostalCode,
  validateMailDeliveryPostOffice,
  validateMailDeliveryStreetAddress,
  validateMailDeliveryStreetAddressExtras,
} from 'todistukset-common/src/vtj/form-validation.util'
import { getValidationErrorFromValidator } from 'todistukset-ui/src/vtj/components/order-wizard/validation'
import LocalizedString from 'todistukset-ui/src/vtj/components/localized-string/LocalizedString.react'
import {
  FormContainer,
  NonEditableField,
  StyledTextInput,
  TextInputs,
} from 'todistukset-ui/src/vtj/components/order-wizard/CommonFormComponents.sc'
import { PostalInformation } from 'todistukset-ui/src/vtj/components/order-wizard/DeliveryMethodStep.sc'
import {
  EditableField,
  editIlmoitettuArvo,
  finalEditableFieldValue,
} from 'todistukset-common/src/vtj/types/editable-field.type'

const DeliveryMethodStep: StepComponent = observer(
  forwardRef(function DeliveryMethodStep({ titleId, certificateType }, ref) {
    if (!isVtjOteTodistus(certificateType)) {
      throw new Error(
        `Type check failed. This step supports only VtjOteTodistusType but ${certificateType} was given.`
      )
    }

    const {
      forms: { [certificateType]: form },
      common: { loggedInUser },
    } = useStore()

    const usingElectronicDelivery = getIsAURA(form)
    const usingMailDelivery = !usingElectronicDelivery

    const isEidas = isEidasIdentifiedUser(loggedInUser.get())

    const mailDeliveryErrorRef = useRef<FirstErrorImperativeHandle>(null)
    const contactInfoErrorRef = useRef<FirstErrorImperativeHandle>(null)
    useImperativeHandle(
      ref,
      () => ({
        getFirstErrorField: () =>
          mailDeliveryErrorRef.current?.getFirstErrorField() ??
          contactInfoErrorRef.current?.getFirstErrorField() ??
          null,
      }),
      []
    )

    return (
      <FormContainer>
        <Block variant="section">
          <Heading variant="h2" id={titleId}>
            <LocalizedString stringKey="orderWizardDeliveryMethodStepTitle" />
          </Heading>
          <Paragraph data-test-id="delivery-method-info">
            {usingMailDelivery && (
              <LocalizedString
                stringKey={
                  isEidas
                    ? 'orderWizardDeliveryMethodStepMainDescriptionMailDeliveryEidas'
                    : 'orderWizardDeliveryMethodStepMainDescriptionMailDelivery'
                }
              />
            )}
            {usingElectronicDelivery && (
              <LocalizedString stringKey="orderWizardDeliveryMethodStepMainDescriptionElectronicDelivery" />
            )}
          </Paragraph>
          {/* Suomi.fi messages are currently not send {usingElectronicDelivery && <SuomifiDeliveryInlineAlert />} */}
        </Block>
        {usingMailDelivery && (
          <>
            <MailDelivery
              certificateType={certificateType}
              ref={mailDeliveryErrorRef}
            />
            <ContactInfo
              certificateType={certificateType}
              ref={contactInfoErrorRef}
            />
          </>
        )}
      </FormContainer>
    )
  })
)

export default DeliveryMethodStep

type SubComponent = React.FC<
  {
    certificateType: VtjOteTodistusType
  } & React.RefAttributes<FirstErrorImperativeHandle>
>

const MailDelivery: SubComponent = observer(
  forwardRef(function MailDelivery({ certificateType }, ref) {
    const {
      common: { loggedInUser },
      forms: { [certificateType]: form },
      steps: {
        [certificateType]: { showStepValidationErrors: showErrors },
      },
    } = useStore()

    const user = loggedInUser.get()

    const { t } = useTranslation()

    const userDefaults = populateAndSanitizeMailDelivery({ user })
    // Previous version of the form had similar logic, and a note in
    // stores/form.mobx.store.ts says that we shouldn't get any address
    // information at all for abroad addresses.
    if (!finalEditableFieldValue(userDefaults.country)) {
      userDefaults.country = editIlmoitettuArvo<EditableField<string>>(
        null,
        finalEditableFieldValue(userDefaults.streetAddress) ? 'Suomi' : ''
      )
    }

    const addressName = `${finalEditableFieldValue(
      userDefaults.firstName
    )} ${finalEditableFieldValue(userDefaults.lastName)}`

    const hasFullAddressInformation =
      addressName &&
      finalEditableFieldValue(userDefaults.streetAddress) &&
      finalEditableFieldValue(userDefaults.postalCode) &&
      finalEditableFieldValue(userDefaults.postOffice) &&
      finalEditableFieldValue(userDefaults.country)

    const hasTurvakielto = user.isSafetyProhibited

    const onChange = useMemo(() => {
      const {
        setStreetAddress,
        setStreetAddressExtras,
        setPostalCode,
        setPostOffice,
        setCountry,
      } = getFormActions(certificateType).mailDelivery
      return {
        streetAddress: setStreetAddress,
        streetAddressExtras: setStreetAddressExtras,
        postalCode: setPostalCode,
        postOffice: setPostOffice,
        country: setCountry,
      }
    }, [certificateType])

    const commonInputProps = {
      type: 'text' as const,
      fullWidth: true,
      statusTextAriaLiveMode: 'polite' as const,
    }

    const refs = useFirstErrorImperativeHandle(
      ref,
      form,
      getMailDeliveryErrors,
      [
        'streetAddress',
        'streetAddressExtras',
        'postalCode',
        'postOffice',
        'country',
      ]
    )

    return (
      <Block variant="section" data-test-id="mail-delivery-section">
        <Heading variant="h3">
          <LocalizedString stringKey="orderWizardDeliveryMethodStepDeliveryAddressTitle" />
        </Heading>
        {hasFullAddressInformation ? (
          <>
            <Paragraph>
              <LocalizedString stringKey="orderWizardDeliveryMethodStepDeliveryAddressFromVtj" />
            </Paragraph>
            <Paragraph data-test-id="delivery-address-prefilled">
              <Text>{addressName}</Text>
              <br />
              <Text>{finalEditableFieldValue(userDefaults.streetAddress)}</Text>
              <br />
              {userDefaults.streetAddressExtras && (
                <>
                  <Text>
                    {finalEditableFieldValue(userDefaults.streetAddressExtras)}
                  </Text>
                  <br />
                </>
              )}
              <Text>
                {finalEditableFieldValue(userDefaults.postalCode)}{' '}
                {finalEditableFieldValue(userDefaults.postOffice)}
              </Text>
              <br />
              <Text>{finalEditableFieldValue(userDefaults.country)}</Text>
            </Paragraph>
          </>
        ) : (
          <>
            {hasTurvakielto && (
              <Paragraph data-test-id="turvakielto-information">
                <LocalizedString stringKey="orderWizardDeliveryMethodStepDeliveryAddressTurvakieltoInformation" />
              </Paragraph>
            )}
            <TextInputs>
              <NonEditableField>
                <Paragraph>
                  <Text variant="bold">
                    <LocalizedString stringKey="orderWizardDeliveryMethodStepInputLabel_name" />
                  </Text>
                </Paragraph>
                <Paragraph>{addressName}</Paragraph>
              </NonEditableField>
              <StyledTextInput
                {...inputId('delivery-address-street-address')}
                {...commonInputProps}
                name="street-address"
                labelText={t(
                  'orderWizardDeliveryMethodStepInputLabel_streetAddress'
                )}
                value={
                  finalEditableFieldValue(form.mailDelivery.streetAddress) || ''
                }
                onChange={onChange.streetAddress}
                visualPlaceholder={t(
                  'orderWizardDeliveryMethodStepPrompt_streetAddress'
                )}
                {...errorStatus({
                  field: 'streetAddress',
                  form,
                  showErrors,
                  t,
                })}
                forwardedRef={refs.streetAddress}
              />

              <StyledTextInput
                {...inputId('delivery-address-street-address-extras')}
                {...commonInputProps}
                name="street-address-extras"
                labelText={t(
                  'orderWizardDeliveryMethodStepInputLabel_streetAddressExtras'
                )}
                value={
                  finalEditableFieldValue(
                    form.mailDelivery.streetAddressExtras
                  ) || ''
                }
                onChange={onChange.streetAddressExtras}
                visualPlaceholder={t(
                  'orderWizardDeliveryMethodStepPrompt_streetAddressExtras'
                )}
                {...errorStatus({
                  field: 'streetAddressExtras',
                  form,
                  showErrors,
                  t,
                })}
                forwardedRef={refs.streetAddressExtras}
                optionalText={t(
                  'orderWizardDeliveryMethodStepInputLabelWhenRequired'
                )}
              />
              <PostalInformation>
                <StyledTextInput
                  {...inputId('delivery-address-postal-code')}
                  {...commonInputProps}
                  name="postal-code"
                  labelText={t(
                    'orderWizardDeliveryMethodStepInputLabel_postalCode'
                  )}
                  value={
                    finalEditableFieldValue(form.mailDelivery.postalCode) || ''
                  }
                  onChange={onChange.postalCode}
                  visualPlaceholder={t(
                    'orderWizardDeliveryMethodStepPrompt_postalCode'
                  )}
                  {...errorStatus({
                    field: 'postalCode',
                    form,
                    showErrors,
                    t,
                  })}
                  forwardedRef={refs.postalCode}
                />

                <StyledTextInput
                  {...inputId('delivery-address-post-office')}
                  {...commonInputProps}
                  name="address-level2"
                  labelText={t(
                    'orderWizardDeliveryMethodStepInputLabel_postOffice'
                  )}
                  value={
                    finalEditableFieldValue(form.mailDelivery.postOffice) || ''
                  }
                  onChange={onChange.postOffice}
                  visualPlaceholder={t(
                    'orderWizardDeliveryMethodStepPrompt_postOffice'
                  )}
                  {...errorStatus({
                    field: 'postOffice',
                    form,
                    showErrors,
                    t,
                  })}
                  forwardedRef={refs.postOffice}
                />
              </PostalInformation>

              <StyledTextInput
                {...inputId('delivery-address-country')}
                {...commonInputProps}
                name="country-name"
                labelText={t('orderWizardDeliveryMethodStepInputLabel_country')}
                value={finalEditableFieldValue(form.mailDelivery.country) || ''}
                onChange={onChange.country}
                visualPlaceholder={t(
                  'orderWizardDeliveryMethodStepPrompt_country'
                )}
                {...errorStatus({
                  field: 'country',
                  form,
                  showErrors,
                  t,
                })}
                forwardedRef={refs.country}
              />
            </TextInputs>
          </>
        )}
      </Block>
    )
  })
)

const ContactInfo: SubComponent = observer(
  forwardRef(function ContactInfo({ certificateType }, ref) {
    // We want to render an error message when user has not specified
    // neither email nor phone number
    // However, we do not want to show this error initially when the
    // page loads. That's why we use these hooks here.
    const {
      forms: { [certificateType]: form },
      steps: {
        [certificateType]: { showStepValidationErrors: showErrors },
      },
    } = useStore()

    const { t } = useTranslation()

    const onChange = useMemo(() => {
      const { setEmail, setPhoneNumber } =
        getFormActions(certificateType).contactInfo
      return {
        phoneNumber: setPhoneNumber,
        email: setEmail,
      }
    }, [certificateType])

    const refs = useFirstErrorImperativeHandle(
      ref,
      form,
      getContactInfoErrors,
      ['phoneNumber', 'email']
    )

    return (
      <Block variant="section" data-test-id="contact-info-section">
        <Heading variant="h3">
          <LocalizedString stringKey="orderWizardDeliveryMethodStepContactInfoTitle" />
        </Heading>
        <Paragraph data-test-id="contact-info-paragraph">
          <LocalizedString stringKey="orderWizardDeliveryMethodStepContactInfoDescription" />
        </Paragraph>
        <TextInputs>
          <StyledTextInput
            {...inputId('contact-info-phone')}
            type="tel"
            name="phone-number"
            labelText={t(
              'orderWizardDeliveryMethodStepInputLabel_contactPhoneNumber'
            )}
            value={finalEditableFieldValue(form.contactInfo.phoneNumber) || ''}
            onChange={onChange.phoneNumber}
            fullWidth
            visualPlaceholder={t(
              'orderWizardDeliveryMethodStepPrompt_contactPhoneNumber'
            )}
            {...errorStatus({
              field: 'phoneNumber',
              form,
              showErrors,
              t,
            })}
            statusTextAriaLiveMode="polite"
            ref={refs.phoneNumber}
          />
          <StyledTextInput
            {...inputId('contact-info-email')}
            type="email"
            name="email"
            labelText={t(
              'orderWizardDeliveryMethodStepInputLabel_contactEmail'
            )}
            value={finalEditableFieldValue(form.contactInfo.email) || ''}
            onChange={onChange.email}
            fullWidth
            visualPlaceholder={t(
              'orderWizardDeliveryMethodStepPrompt_contactEmail'
            )}
            {...errorStatus({
              field: 'email',
              form,
              showErrors,
              t,
            })}
            statusTextAriaLiveMode="polite"
            ref={refs.email}
            optionalText={t('orderWizardDeliveryMethodStepInputLabelOptional')}
          />
        </TextInputs>
      </Block>
    )
  })
)

const inputId = (fieldId: string) => {
  return {
    id: fieldId,
    'data-test-id': fieldId,
  }
}

const errorStatus = (params: {
  field:
    | keyof ReturnType<typeof getMailDeliveryErrors>
    | keyof ReturnType<typeof getContactInfoErrors>
  form: TodistusStoreForm
  showErrors: boolean
  t: TFunction
}) =>
  getErrorStatus({
    ...params,
    errors: {
      ...getMailDeliveryErrors(params.form),
      ...getContactInfoErrors(params.form),
    },
  })

function getMailDeliveryErrors(form: TodistusStoreForm) {
  return {
    streetAddress: getValidationErrorFromValidator(
      validateMailDeliveryStreetAddress,
      finalEditableFieldValue(form.mailDelivery.streetAddress),
      form
    ),
    streetAddressExtras: getValidationErrorFromValidator(
      validateMailDeliveryStreetAddressExtras,
      finalEditableFieldValue(form.mailDelivery.streetAddressExtras),
      form
    ),
    postalCode: getValidationErrorFromValidator(
      validateMailDeliveryPostalCode,
      finalEditableFieldValue(form.mailDelivery.postalCode),
      form
    ),
    postOffice: getValidationErrorFromValidator(
      validateMailDeliveryPostOffice,
      finalEditableFieldValue(form.mailDelivery.postOffice),
      form
    ),
    country: getValidationErrorFromValidator(
      validateMailDeliveryCountry,
      finalEditableFieldValue(form.mailDelivery.country),
      form
    ),
  }
}

function getContactInfoErrors(form: TodistusStoreForm) {
  return {
    phoneNumber: getValidationErrorFromValidator(
      validateContactInfoPhoneNumber,
      form.contactInfo.phoneNumber?.ilmoitettuArvo,
      form
    ),
    email: getValidationErrorFromValidator(
      validateContactInfoEmail,
      form.contactInfo.email?.ilmoitettuArvo,
      form
    ),
  }
}
